<template>
  <Dialog :is-open="isOpen" title="Actions" width="300px" @close="closeDialog">
    <div :class="$style.actionsContent">
      <button :class="$style.actionButton" @click="handleDownload">
        <DownloadIcon :size="14" />
        Download Tasks
      </button>

      <button :class="$style.actionButton" @click="handleUpload">
        <UploadIcon :size="14" />
        Upload Tasks
      </button>

      <button :class="$style.actionButton" @click="showTopicManager = true">
        Manage Topics
      </button>

      <button
        :class="$style.actionButton"
        @click="showOverrunReasonManager = true"
      >
        Manage Overrun Reasons
      </button>

      <div :class="$style.saturationSection">
        <div :class="$style.saturationLabel">
          <BrightnessIcon :size="16" />
          Saturation of Task Colors
        </div>
        <Slider
          :model-value="saturation"
          :min="0"
          :max="200"
          :show-value="true"
          :value-formatter="(value) => `${value}%`"
          @update:modelValue="handleSaturationChange"
        />
      </div>

      <div :class="$style.separator"></div>

      <button :class="$style.actionButton" @click="handleReset">
        <DeleteIcon :size="14" />
        Reset All
      </button>

      <button :class="$style.actionButton" @click="handlePrivacy">
        <ClipboardIcon :size="14" />
        Privacy & Analytics
      </button>

      <!-- Development only: Load Default Data -->
      <button
        v-if="isDevelopment"
        :class="$style.actionButton"
        @click="handleLoadDefaultData"
      >
        <DownloadIcon :size="14" />
        Load Default Data
      </button>
    </div>

    <!-- Topic Manager Modal -->
    <div
      v-if="showTopicManager"
      :class="$style.modalOverlay"
      @click="closeTopicManager"
    >
      <div :class="$style.modalContent" @click.stop>
        <TopicManager @close="closeTopicManager" />
      </div>
    </div>

    <!-- Overrun Reason Manager Modal -->
    <OverrunReasonManager
      :is-open="showOverrunReasonManager"
      @close="closeOverrunReasonManager"
    />

    <input
      type="file"
      ref="fileInput"
      accept=".json"
      style="display: none"
      @change="onFileChange"
    />
  </Dialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import Dialog from "./Dialog.vue";
import DeleteIcon from "./DeleteIcon.vue";
import TopicManager from "./TopicManager.vue";
import OverrunReasonManager from "./OverrunReasonManager.vue";
import Slider from "./Slider.vue";
import {
  DownloadIcon,
  UploadIcon,
  BrightnessIcon,
  ClipboardIcon,
} from "./icons";

defineProps<{
  isOpen: boolean;
  saturation: number;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "download"): void;
  (e: "upload", file: File): void;
  (e: "reset"): void;
  (e: "saturation-change", saturation: number): void;
  (e: "load-default-data"): void;
  (e: "privacy"): void;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const showTopicManager = ref(false);
const showOverrunReasonManager = ref(false);

// Check if we're in development mode
const isDevelopment =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname.includes("localhost");

function closeDialog() {
  emit("close");
}

function handleDownload() {
  emit("download");
  closeDialog();
}

function handleUpload() {
  fileInput.value?.click();
}

function handleReset() {
  emit("reset");
  closeDialog();
}

function handleLoadDefaultData() {
  emit("load-default-data");
  closeDialog();
}

function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    emit("upload", file);
    closeDialog();
    // Reset the input so the same file can be selected again
    target.value = "";
  }
}

function handleSaturationChange(newSaturation: number) {
  emit("saturation-change", newSaturation);
}

function closeTopicManager() {
  showTopicManager.value = false;
}

function closeOverrunReasonManager() {
  showOverrunReasonManager.value = false;
}

function handlePrivacy() {
  emit("privacy");
  closeDialog();
}
</script>

<style module>
.actionsContent {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.actionButton {
  padding: 12px 16px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  transition: background-color 0.2s ease;
  text-align: left;
  border-radius: 6px;
}

.actionButton:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.separator {
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin: 8px 0;
}

.saturationSection {
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  margin: 8px 0;
}

.saturationLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 12px;
}

button {
  all: unset;
}

.modalOverlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modalContent {
  background: #2c3e50;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 90%;
  max-width: 600px;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .actionButton {
    padding: 14px 16px;
    font-size: 16px;
  }

  .saturationSection {
    padding: 16px;
  }
}
</style>
